/**
 * Event Delegation Module
 *
 * Consolidates all delegated event handling for the grid.
 * Uses event delegation (single listener on container) rather than per-cell/per-row
 * listeners to minimize memory usage.
 *
 * This module provides:
 * - setupCellEventDelegation: Body-level handlers (mousedown, click, dblclick on cells/rows)
 * - setupRootEventDelegation: Root-level handlers (keydown, mousedown for plugins, drag tracking)
 *
 * Edit triggering is handled separately by the EditingPlugin via
 * onCellClick and onKeyDown hooks.
 */

import { GridClasses, GridDataAttrs } from '../constants';
import type { CellMouseEvent } from '../plugin/types';
import type { GridHost, InternalGrid } from '../types';
import { handleGridKeyDown } from './keyboard';
import { startPointerDrag } from './pointer-drag';
import { getPrimaryPointer } from './pointer-modality';
import { handleRowClick } from './rows';
import { clearCellFocus, getColIndexFromCell, getRowIndexFromCell } from './utils';
import { readCellField } from './value-accessor';

// #region Utilities
// Track drag state per grid instance (avoids polluting InternalGrid interface)
const dragState = new WeakMap<InternalGrid, boolean>();

/** Marks a `title` the grid put there itself, so an author's own title is never clobbered. */
const OWNED_TITLE_ATTR = GridDataAttrs.TRUNCATED;

/**
 * Give an ellipsised cell a way to be read in full.
 *
 * WCAG 2.2 SC 1.4.12 Text Spacing allows truncation only while "the content is
 * still available" — user-applied letter/word spacing pushes text past a fixed
 * column width, so cells that fit before the override stop fitting after it.
 * The native `title` is that mechanism, and it costs nothing until a pointer
 * actually rests on a cell: resolving it here rather than at render time keeps
 * the hot path untouched and leaves cells that do fit tooltip-free.
 */
function syncTruncationTitle(cell: HTMLElement): void {
  const owned = cell.hasAttribute(OWNED_TITLE_ATTR);
  if (cell.title && !owned) return;

  const text = cell.scrollWidth > cell.clientWidth ? (cell.textContent ?? '').trim() : '';
  if (text) {
    cell.title = text;
    cell.setAttribute(OWNED_TITLE_ATTR, '');
  } else if (owned) {
    cell.removeAttribute('title');
    cell.removeAttribute(OWNED_TITLE_ATTR);
  }
}
// #endregion

// #region Cell Mouse Handlers
/**
 * Handle delegated mousedown on cells.
 * Updates focus position for navigation.
 *
 * IMPORTANT: This must NOT call refreshVirtualWindow or any function that
 * re-renders DOM elements. Doing so would replace the element the user clicked on,
 * causing the subsequent click event to fire on a detached element and not bubble
 * to parent handlers (like handleRowClick).
 *
 * For mouse interactions, the cell is already visible (user clicked on it),
 * so we only need to update focus state without scrolling or re-rendering.
 */
function handleCellMousedown(grid: InternalGrid, cell: HTMLElement): void {
  const rowIndex = getRowIndexFromCell(cell);
  const colIndex = getColIndexFromCell(cell);
  if (rowIndex < 0 || colIndex < 0) return;

  grid._focusRow = rowIndex;
  grid._focusCol = colIndex;

  // Update focus styling directly without triggering re-render.
  // ensureCellVisible() would call refreshVirtualWindow() which replaces DOM elements,
  // breaking the click event that follows this mousedown.
  clearCellFocus(grid._bodyEl);
  cell.classList.add('cell-focus');
  cell.setAttribute('aria-selected', 'true');

  // Ensure the grid element has DOM focus so keyboard events (like Ctrl+C for clipboard)
  // bubble through the grid's keydown listener. Without this, clicking on non-focusable
  // cells may leave focus elsewhere, making keyboard shortcuts unreachable.
  // Always call focus() — even when activeElement is inside the grid — because
  // browser default behaviors (e.g., shift+click text selection, cell re-pooling)
  // can move focus to document.body between mousedown and the next keydown.
  const gridEl = cell.closest('tbw-grid, [data-tbw-grid]') as HTMLElement | null;
  if (gridEl && document.activeElement !== gridEl) {
    gridEl.focus({ preventScroll: true });
  }
}
// #endregion

// #region Mouse Event Building
/**
 * Build a CellMouseEvent from a native MouseEvent.
 * Extracts cell/row information from the event target.
 */
function buildCellMouseEvent(
  grid: InternalGrid,
  renderRoot: HTMLElement,
  e: MouseEvent,
  type: 'mousedown' | 'mousemove' | 'mouseup',
): CellMouseEvent {
  // For document-level events (mousemove/mouseup during drag), e.target won't be inside shadow DOM.
  // Use composedPath to find elements inside shadow roots, or fall back to elementFromPoint.
  let target: Element | null = null;

  // composedPath gives us the full path including shadow DOM elements
  const path = e.composedPath?.() as Element[] | undefined;
  if (path && path.length > 0) {
    target = path[0];
  } else {
    target = e.target as Element;
  }

  // Resolve the element actually under the pointer when the event target can't
  // identify a cell — either because it is outside the grid (document-level
  // events) or because pointer capture retargeted the event to the capture
  // element. Without this a captured drag reports the same cell for every move.
  if (!target || !renderRoot.contains(target) || !target.closest?.('[data-col]')) {
    const elAtPoint = document.elementFromPoint(e.clientX, e.clientY);
    if (elAtPoint) {
      target = elAtPoint;
    }
  }

  // Cells have data-col and data-row attributes
  const cellEl = target?.closest?.('[data-col]') as HTMLElement | null;
  const rowEl = target?.closest?.('.data-grid-row') as HTMLElement | null;
  const headerEl = target?.closest?.('.header-row') as HTMLElement | null;

  let rowIndex: number | undefined;
  let colIndex: number | undefined;
  let row: unknown;
  let field: string | undefined;
  let value: unknown;
  let column: unknown;

  if (cellEl) {
    // Get indices from cell attributes
    rowIndex = parseInt(cellEl.getAttribute('data-row') ?? '-1', 10);
    colIndex = parseInt(cellEl.getAttribute('data-col') ?? '-1', 10);
    if (rowIndex >= 0 && colIndex >= 0) {
      row = grid._rows[rowIndex];
      // colIndex from data-col is a visible-column index (rendering uses _visibleColumns)
      column = grid._visibleColumns[colIndex];
      field = (column as { field?: string })?.field;
      value = row && field ? readCellField(row, field) : undefined;
    }
  }

  return {
    type,
    row,
    rowIndex: rowIndex !== undefined && rowIndex >= 0 ? rowIndex : undefined,
    colIndex: colIndex !== undefined && colIndex >= 0 ? colIndex : undefined,
    field,
    value,
    column: column as CellMouseEvent['column'],
    originalEvent: e,
    cellElement: cellEl ?? undefined,
    rowElement: rowEl ?? undefined,
    isHeader: !!headerEl,
    cell:
      rowIndex !== undefined && colIndex !== undefined && rowIndex >= 0 && colIndex >= 0
        ? { row: rowIndex, col: colIndex }
        : undefined,
  };
}
// #endregion

// #region Drag Tracking
/**
 * Long-press duration (ms) before a coarse-pointer press promotes to a
 * range-paint drag. Matches the iOS Numbers / Google Sheets idiom.
 */
const LONG_PRESS_MS = 400;

/**
 * Movement (px) a fine pointer must travel before a cell press becomes a
 * range-paint drag.
 *
 * Must stay above zero: promotion takes pointer capture, and a captured
 * pointer makes the browser retarget `mouseup` / `click` / `dblclick` to the
 * capture element instead of the cell — which silently disables
 * double-click-to-edit and every other click-driven cell feature.
 */
const DRAG_THRESHOLD_PX = 3;

/**
 * True when the press came from a coarse pointer (finger or stylus).
 *
 * `pointerType` is per-event and therefore more accurate than the
 * `(pointer: coarse)` media query on hybrid devices (e.g. a Surface with both
 * a touchscreen and a mouse), so it is preferred here. `getPrimaryPointer()`
 * is the fallback for synthetic events that omit `pointerType`.
 */
function isCoarsePointer(e: PointerEvent): boolean {
  if (e.pointerType === 'touch' || e.pointerType === 'pen') return true;
  if (e.pointerType === 'mouse') return false;
  return getPrimaryPointer() === 'coarse';
}

/**
 * Suppress native panning for the duration of a range-paint drag.
 *
 * Applied only once the drag is actually promoted — before that the browser
 * must remain free to scroll, otherwise a simple swipe over the grid would be
 * swallowed. Returns a restore function.
 */
function suppressTouchScroll(bodyEl: HTMLElement | undefined): () => void {
  if (!bodyEl) return () => undefined;
  const prev = bodyEl.style.touchAction;
  bodyEl.style.touchAction = 'none';
  return () => {
    bodyEl.style.touchAction = prev;
  };
}

/**
 * How long a synthesised `contextmenu` is suppressed after a claimed long-press.
 *
 * Browsers fire their long-press `contextmenu` at roughly 500 ms, i.e. shortly
 * after our own {@link LONG_PRESS_MS} promotion. The window only needs to span
 * that gap.
 */
const CONTEXT_MENU_SUPPRESS_MS = 700;

/**
 * Swallow the browser's own long-press `contextmenu` for a short window.
 *
 * This is what makes the epic's long-press priority order real rather than
 * aspirational: when a plugin (selection mode, range paint) has *claimed* a
 * coarse long-press, the browser would otherwise still synthesise a
 * `contextmenu` from the same gesture ~100 ms later and `ContextMenuPlugin`
 * would pop a menu on top of it.
 *
 * The listener is registered on `document` in the capture phase so it always
 * precedes `ContextMenuPlugin`'s own listener (bound on `.tbw-grid-root`), and
 * removes itself on the first event *originating inside this grid* or when the
 * window expires — whichever comes first. When nothing claims the press, this is
 * never called and the native menu opens exactly as it does on the right-click
 * path.
 *
 * Capture-phase on `document` is required (it must beat `ContextMenuPlugin`),
 * but the suppression itself is scoped to `renderRoot`: a `contextmenu` raised
 * anywhere else on the page — another grid, or the host application's own
 * menu — passes through untouched, and does not consume the one-shot window.
 */
function suppressNextContextMenu(renderRoot: HTMLElement): void {
  const doc = renderRoot.ownerDocument;
  if (!doc) return;

  let timer: ReturnType<typeof setTimeout> | null = null;
  const onContextMenu = (e: Event): void => {
    const target = e.target as Node | null;
    if (!target || !renderRoot.contains(target)) return;
    e.preventDefault();
    e.stopPropagation();
    cleanup();
  };
  const cleanup = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    doc.removeEventListener('contextmenu', onContextMenu, true);
  };

  doc.addEventListener('contextmenu', onContextMenu, true);
  timer = setTimeout(cleanup, CONTEXT_MENU_SUPPRESS_MS);
}

/**
 * Handle `pointerdown` and dispatch to the plugin system.
 *
 * Fine pointers (mouse / precision trackpad) keep the historical behaviour: the
 * `mousedown` hook is dispatched immediately and any plugin that claims it
 * starts a drag on the very next move.
 *
 * Coarse pointers (touch / stylus) defer the dispatch until a {@link LONG_PRESS_MS}
 * long-press has elapsed, so that taps and scroll swipes are never mistaken for
 * a range-paint. Movement beyond the slop before the timer fires aborts the
 * gesture entirely and lets the browser scroll.
 */
function handlePointerDown(grid: InternalGrid, renderRoot: HTMLElement, e: PointerEvent): void {
  // Only primary presses start a drag; secondary buttons belong to ContextMenuPlugin.
  if (e.button !== 0 && e.pointerType === 'mouse') return;
  if (dragState.get(grid)) return;

  const coarse = isCoarsePointer(e);
  let restoreTouchAction: (() => void) | null = null;

  /** Dispatch the `mousedown` hook; returns whether a plugin claimed the press. */
  const dispatchDown = (): boolean => {
    const event = buildCellMouseEvent(grid, renderRoot, e, 'mousedown');
    return grid._dispatchCellMouseDown?.(event) ?? false;
  };

  if (!coarse) {
    // Fine pointer — dispatch up front, exactly as the mouse implementation did.
    if (!dispatchDown()) return;
    dragState.set(grid, true);
  }

  startPointerDrag(
    e,
    renderRoot,
    {
      onPromote: () => {
        if (coarse) {
          // Long-press recognised — only now does the press become a drag.
          // If no plugin claims it, we deliberately do nothing: the browser is
          // then free to synthesise its own `contextmenu` from the same
          // long-press, which is the documented fallback (see below).
          if (!dispatchDown()) return;
          dragState.set(grid, true);
          restoreTouchAction = suppressTouchScroll(grid._bodyEl);
          suppressNextContextMenu(renderRoot);
        }
      },
      onMove: (moveEvent) => {
        if (!dragState.get(grid)) return;
        const event = buildCellMouseEvent(grid, renderRoot, moveEvent, 'mousemove');
        grid._dispatchCellMouseMove?.(event);
      },
      onEnd: (upEvent) => {
        restoreTouchAction?.();
        restoreTouchAction = null;
        if (!dragState.get(grid)) return;
        const event = buildCellMouseEvent(grid, renderRoot, upEvent, 'mouseup');
        grid._dispatchCellMouseUp?.(event);
        dragState.set(grid, false);
      },
      onCancel: () => {
        restoreTouchAction?.();
        restoreTouchAction = null;
        dragState.set(grid, false);
      },
    },
    // Coarse presses must be held; fine presses promote after a small move.
    coarse ? { longPressDuration: LONG_PRESS_MS } : { threshold: DRAG_THRESHOLD_PX },
  );
}
// #endregion

// #region Setup Functions
/**
 * Set up delegated event listeners on the grid body.
 * Consolidates all row/cell mouse event handling into a single set of listeners.
 * Call once during grid initialization.
 *
 * Benefits:
 * - 3 listeners total vs N*2 listeners (where N = pool size)
 * - Consistent event handling across all rows
 * - Automatic cleanup via AbortController signal
 *
 * @param grid - The grid instance
 * @param bodyEl - The .rows element containing all data rows
 * @param signal - AbortSignal for cleanup
 */
export function setupCellEventDelegation(grid: GridHost, bodyEl: HTMLElement, signal: AbortSignal): void {
  // Mousedown - update focus on any cell (not just editable)
  bodyEl.addEventListener(
    'mousedown',
    (e) => {
      const cell = (e.target as HTMLElement).closest('.cell[data-col]') as HTMLElement | null;
      if (!cell) return;

      // Skip if clicking inside an editing cell (let the editor handle it)
      if (cell.classList.contains(GridClasses.EDITING)) return;

      // Skip preventDefault when clicking a draggable element (or its children).
      // Native HTML5 drag-and-drop requires mousedown to NOT be prevented;
      // otherwise the browser never fires dragstart.
      const target = e.target as HTMLElement;
      const isDraggable = target.draggable || target.closest('[draggable="true"]');

      // Prevent the browser from managing focus for grid cells.
      // Without this, the browser can steal focus (e.g., shift+click extends
      // a native text selection, moving activeElement to document.body).
      // We manage focus explicitly via handleCellMousedown → gridEl.focus().
      if (!isDraggable) {
        e.preventDefault();
      }

      handleCellMousedown(grid, cell);
    },
    { signal },
  );

  // Click - handle row/cell click interactions
  bodyEl.addEventListener(
    'click',
    (e) => {
      const rowEl = (e.target as HTMLElement).closest('.data-grid-row') as HTMLElement | null;
      if (rowEl) handleRowClick(grid, e as MouseEvent, rowEl);

      // After click handling: keep focus on the grid element (not individual cells).
      // In a virtualized grid, cells can be detached by subsequent render cycles
      // (e.g., SelectionPlugin's requestAfterRender → RAF render → row recycling).
      // A detached focused cell causes activeElement to revert to <body>, breaking
      // keyboard shortcuts like Ctrl+C. The grid element (tabindex=0) is stable
      // and receives all keyboard events via bubble phase.
      // Skip if an editor is active — editors manage their own focus.
      if (!document.activeElement?.closest(`.cell.${GridClasses.EDITING}`)) {
        const gridEl = (e.target as HTMLElement).closest('tbw-grid, [data-tbw-grid]') as HTMLElement | null;
        if (gridEl) gridEl.focus({ preventScroll: true });
      }
    },
    { signal },
  );

  // Dblclick - same handler as click (edit triggering handled by EditingPlugin)
  bodyEl.addEventListener(
    'dblclick',
    (e) => {
      const rowEl = (e.target as HTMLElement).closest('.data-grid-row') as HTMLElement | null;
      if (rowEl) handleRowClick(grid, e as MouseEvent, rowEl);
    },
    { signal },
  );
}

/**
 * Set up root-level and document-level event listeners.
 * These are added once per grid lifetime (not re-attached on DOM recreation).
 *
 * Includes:
 * - keydown: Keyboard navigation (arrows, Enter, Escape)
 * - mousedown: Plugin dispatch for cell interactions
 * - mousemove/mouseup: Global drag tracking
 *
 * @param grid - The grid instance
 * @param gridElement - The grid element (for keydown)
 * @param renderRoot - The render root element (for mousedown)
 * @param signal - AbortSignal for cleanup
 */
export function setupRootEventDelegation(
  grid: GridHost,
  gridElement: HTMLElement,
  renderRoot: HTMLElement,
  signal: AbortSignal,
): void {
  // Element-level keydown handler for keyboard navigation
  gridElement.addEventListener('keydown', (e) => handleGridKeyDown(grid, e), { signal });

  // Central pointer event handling for plugins. Pointer capture keeps the whole
  // drag on the render root, so no document-level move/up listeners are needed.
  renderRoot.addEventListener('pointerdown', (e) => handlePointerDown(grid, renderRoot, e as PointerEvent), {
    signal,
  });

  // Reveal text the cell had to ellipsise (SC 1.4.12). The Tooltip plugin does
  // the same job with a styled, keyboard-reachable, Escape-dismissible popover,
  // so stand down whenever it is installed rather than stacking two tooltips.
  renderRoot.addEventListener(
    'mouseover',
    (e) => {
      if (grid.getPluginByName?.('tooltip')) return;
      const cell = (e.target as HTMLElement).closest('.cell') as HTMLElement | null;
      if (!cell) return;
      // mouseover re-fires for every child boundary crossed inside the cell, and
      // `scrollWidth` forces layout — so measure only on a genuine cell entry.
      const from = e.relatedTarget as Node | null;
      if (from && cell.contains(from)) return;
      syncTruncationTitle(cell);
    },
    { signal, passive: true },
  );
}
// #endregion
